import React, { useCallback, useEffect, useMemo, useState } from "react";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TablePagination,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import AdminSlidingService from "../AdminSlidingService";
import Logger from "../../../services/Logger";
import StatsSlidingService from "../../../services/StatsSlidingService";
import StatsSummaryTile from "../../pzl_common/stats/StatsSummaryTile";

const RANGE_OPTIONS = [
    { value: "all", label: "All time" },
    { value: "7d", label: "Last 7 days", days: 7 },
    { value: "1m", label: "Last month", days: 30 },
    { value: "3m", label: "Last 3 months", days: 90 },
    { value: "12m", label: "Last 12 months", days: 365 },
];

const DEFAULT_RANGE = "all";
const DEFAULT_SORT_MODEL = [{ field: "completedPlays", sort: "desc" }];
const RANGE_STORAGE_KEY = "admin.sliding.topPuzzles.range";
const VIEW_STORAGE_KEY = "admin.sliding.topPuzzles.view";
const SORT_STORAGE_KEY = "admin.sliding.topPuzzles.sort";

const ORDER_BY_OPTIONS = [
    { value: "completedPlays:desc", label: "Completed (most)", field: "completedPlays", dir: "desc" },
    { value: "completedPlays:asc", label: "Completed (least)", field: "completedPlays", dir: "asc" },
    { value: "plays:desc", label: "Plays (most)", field: "plays", dir: "desc" },
    { value: "plays:asc", label: "Plays (least)", field: "plays", dir: "asc" },
    { value: "publishedAt:desc", label: "Published at (newest)", field: "publishedAt", dir: "desc" },
    { value: "publishedAt:asc", label: "Published at (oldest)", field: "publishedAt", dir: "asc" },
    { value: "createdAt:desc", label: "Created at (newest)", field: "createdAt", dir: "desc" },
    { value: "createdAt:asc", label: "Created at (oldest)", field: "createdAt", dir: "asc" },
];

const VALID_SORT_VALUES = new Set(ORDER_BY_OPTIONS.map((option) => option.value));
const DEFAULT_ORDER_BY = "completedPlays:desc";

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "\u2014");

const formatDateTime = (epochMillis) =>
    epochMillis ? dayjs(Number(epochMillis)).format("YYYY-MM-DD HH:mm") : "\u2014";

const rangeDays = (value) => RANGE_OPTIONS.find((option) => option.value === value)?.days ?? null;

function orderByValue(field, dir) {
    return `${field}:${dir === "asc" ? "asc" : "desc"}`;
}

function resolveOrderByOption(value) {
    return ORDER_BY_OPTIONS.find((option) => option.value === value) || ORDER_BY_OPTIONS[0];
}

function sortModelFromOrderBy(value) {
    const option = resolveOrderByOption(value);
    if (option.field === "completedPlays" || option.field === "plays") {
        return [{ field: option.field, sort: option.dir }];
    }
    return [];
}

function loadStoredRange() {
    try {
        const stored = window.localStorage.getItem(RANGE_STORAGE_KEY);
        return RANGE_OPTIONS.some((option) => option.value === stored) ? stored : DEFAULT_RANGE;
    } catch {
        return DEFAULT_RANGE;
    }
}

function loadStoredView() {
    try {
        const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
        return stored === "card" || stored === "list" ? stored : "list";
    } catch {
        return "list";
    }
}

function loadStoredOrderBy() {
    try {
        const stored = window.localStorage.getItem(SORT_STORAGE_KEY);
        return VALID_SORT_VALUES.has(stored) ? stored : DEFAULT_ORDER_BY;
    } catch {
        return DEFAULT_ORDER_BY;
    }
}

function stateChipColor(state) {
    const s = String(state || "").toUpperCase();
    if (s === "PUBLISHED") return "success";
    if (s === "REVIEWED") return "info";
    if (s === "DISABLED") return "warning";
    return "default";
}

function isTopFiveRank(row) {
    const rank = Number(row?.rank);
    return Number.isFinite(rank) && rank >= 1 && rank <= 5;
}

/** Amber - top completed ranks 1-5 (phone "top" list when ordered by completed). */
const TOP_FIVE_BG = "rgba(245, 158, 11, 0.18)";
const TOP_FIVE_BG_HOVER = "rgba(245, 158, 11, 0.28)";
/** Teal - editor's picks (special phone section). */
const EDITORS_CHOICE_BG = "rgba(13, 148, 136, 0.16)";
const EDITORS_CHOICE_BG_HOVER = "rgba(13, 148, 136, 0.26)";

function cardHighlightSx(topFive, editorsPicks) {
    if (editorsPicks) {
        return {
            bgcolor: EDITORS_CHOICE_BG,
            outline: "2px solid",
            outlineColor: "teal",
        };
    }
    if (topFive) {
        return {
            bgcolor: TOP_FIVE_BG,
            outline: "2px solid",
            outlineColor: "warning.main",
        };
    }
    return {
        bgcolor: "transparent",
        outline: "none",
        outlineColor: "transparent",
    };
}

function TopPuzzleCard({ row, choiceBusy, highlightTopFive, onEditorsPicks, onFreeChange, onOpen }) {
    const storyId = row?.storyId;
    const tags = Array.isArray(row?.tags) ? row.tags : [];
    const imageUrl = row?.imageUrl;
    const title = row?.title || storyId || "Puzzle";
    const editorsPicks = row?.editorsPicks === true;
    /** Top-5 amber only when not editor's picks - avoid the same puzzle in both special looks. */
    const topFive = highlightTopFive && isTopFiveRank(row) && !editorsPicks;

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 1.5,
                p: 0.75,
                ...cardHighlightSx(topFive, editorsPicks),
            }}
        >
            <Box
                onClick={() => storyId && onOpen?.(storyId)}
                sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: 1,
                    overflow: "hidden",
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "action.hover",
                    cursor: storyId ? "pointer" : "default",
                }}
            >
                {imageUrl ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={title}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            No image
                        </Typography>
                    </Box>
                )}
                <Chip
                    size="small"
                    label={`#${row?.rank ?? "\u2014"}`}
                    sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        fontWeight: 700,
                        bgcolor: topFive ? "warning.main" : "rgba(0,0,0,0.72)",
                        color: topFive ? "warning.contrastText" : "common.white",
                    }}
                />
                {editorsPicks ? (
                    <Chip
                        size="small"
                        label="Editor"
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            fontWeight: 700,
                            bgcolor: "teal",
                            color: "common.white",
                        }}
                    />
                ) : null}
            </Box>

            <Box
                sx={{
                    mt: 0.75,
                    px: 0.25,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                        minHeight: 36,
                    }}
                >
                    <Tooltip title="Editor's picks - special phone section" arrow>
                        <Checkbox
                            size="small"
                            checked={editorsPicks}
                            disabled={!storyId || choiceBusy.has(storyId)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => {
                                event.stopPropagation();
                                onEditorsPicks?.(storyId, event.target.checked);
                            }}
                            slotProps={{ input: { "aria-label": "Editor's picks" } }}
                            sx={{ p: 0.5 }}
                        />
                    </Tooltip>
                    <Tooltip title={row?.free === true ? "Free — uncheck to set paid" : "Paid — check to set free"} arrow>
                        <Checkbox
                            size="small"
                            checked={row?.free === true}
                            disabled={!storyId || choiceBusy.has(storyId)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => {
                                event.stopPropagation();
                                onFreeChange?.(storyId, event.target.checked);
                            }}
                            slotProps={{ input: { "aria-label": "Free" } }}
                            sx={{ p: 0.5 }}
                        />
                    </Tooltip>
                    <Typography
                        variant="body2"
                        noWrap
                        title={title}
                        onClick={() => storyId && onOpen?.(storyId)}
                        sx={{
                            fontWeight: 600,
                            flex: 1,
                            minWidth: 0,
                            cursor: storyId ? "pointer" : "default",
                            lineHeight: 1.3,
                        }}
                    >
                        {title}
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minHeight: 22 }}>
                    {tags.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                            {"\u2014"}
                        </Typography>
                    ) : (
                        tags.slice(0, 3).map((tag) => (
                            <Chip
                                key={tag}
                                size="small"
                                variant="outlined"
                                label={tag}
                                sx={{ height: 20, fontSize: "0.7rem", maxWidth: "100%" }}
                            />
                        ))
                    )}
                    {tags.length > 3 ? (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`+${tags.length - 3}`}
                            sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                    ) : null}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {formatNumber(row?.completedPlays)} done · {formatNumber(row?.plays)} plays
                </Typography>
            </Box>
        </Box>
    );
}

export default function SlidingTopPuzzlesPanel({ language }) {
    const navigate = useNavigate();
    const [range, setRange] = useState(loadStoredRange);
    const [viewMode, setViewMode] = useState(loadStoredView);
    const [orderBy, setOrderBy] = useState(loadStoredOrderBy);
    const [excludeTest, setExcludeTest] = useState(true);
    const [excludeStaff, setExcludeStaff] = useState(true);
    const [editorsPicksOnly, setEditorsPicksOnly] = useState(false);
    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
    const [sortModel, setSortModel] = useState(() => sortModelFromOrderBy(loadStoredOrderBy()));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [choiceError, setChoiceError] = useState(null);
    const [choiceBusy, setChoiceBusy] = useState(() => new Set());
    const [result, setResult] = useState(null);

    useEffect(() => {
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [language]);

    const resetPage = useCallback((update) => {
        update();
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, []);

    const handleRangeChange = useCallback((value) => {
        resetPage(() => {
            setRange(value);
            try {
                window.localStorage.setItem(RANGE_STORAGE_KEY, value);
            } catch {
                /* private mode */
            }
        });
    }, [resetPage]);

    const handleViewModeChange = useCallback((_event, next) => {
        if (!next) return;
        setViewMode(next);
        try {
            window.localStorage.setItem(VIEW_STORAGE_KEY, next);
        } catch {
            /* private mode */
        }
    }, []);

    const handleOrderByChange = useCallback((value) => {
        const next = VALID_SORT_VALUES.has(value) ? value : DEFAULT_ORDER_BY;
        resetPage(() => {
            setOrderBy(next);
            setSortModel(sortModelFromOrderBy(next));
            try {
                window.localStorage.setItem(SORT_STORAGE_KEY, next);
            } catch {
                /* private mode */
            }
        });
    }, [resetPage]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const days = rangeDays(range);
                const option = resolveOrderByOption(orderBy);
                const params = {
                    language,
                    page: pageModel.page,
                    pageSize: pageModel.pageSize,
                    includeTest: !excludeTest,
                    excludeStaff,
                    editorsPicksOnly,
                    sortField: option.field,
                    sortDir: option.dir,
                };
                if (days != null) {
                    const end = dayjs();
                    params.end = end.unix();
                    params.start = end.subtract(days, "day").startOf("day").unix();
                }
                const data = await StatsSlidingService.topPuzzles(params);
                if (!cancelled) {
                    setResult(data ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load sliding top puzzles:", err);
                if (!cancelled) {
                    setError("Failed to load top puzzles.");
                    setResult(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [language, excludeTest, excludeStaff, editorsPicksOnly, pageModel.page, pageModel.pageSize, range, orderBy]);

    const rows = useMemo(() => (Array.isArray(result?.puzzles) ? result.puzzles : []), [result]);
    const rowCount = Number(result?.total) || 0;
    /** Ranks 1-5 matter for completed leaderboard (and editor's-picks phone slots). */
    const highlightTopFive =
        orderBy === "completedPlays:desc" || editorsPicksOnly;

    const handleEditorsPicks = useCallback(async (storyId, checked) => {
        if (!storyId) return;
        setChoiceError(null);
        setChoiceBusy((prev) => {
            const next = new Set(prev);
            next.add(storyId);
            return next;
        });
        const previous = rows.find((row) => row.storyId === storyId)?.editorsPicks === true;
        setResult((prev) => {
            if (!prev || !Array.isArray(prev.puzzles)) return prev;
            if (editorsPicksOnly && !checked) {
                const puzzles = prev.puzzles.filter((row) => row.storyId !== storyId);
                return {
                    ...prev,
                    puzzles,
                    total: Math.max(0, (Number(prev.total) || 0) - 1),
                };
            }
            return {
                ...prev,
                puzzles: prev.puzzles.map((row) =>
                    row.storyId === storyId ? { ...row, editorsPicks: checked } : row
                ),
            };
        });
        try {
            const response = await AdminSlidingService.setStoryEditorsPicks(storyId, checked);
            if (!response?.success) {
                throw new Error(response?.error || "Failed to update editor's picks");
            }
        } catch (err) {
            Logger.error("Failed to update sliding editor's picks:", err);
            setChoiceError("Failed to update editor's picks.");
            setResult((prev) => {
                if (!prev || !Array.isArray(prev.puzzles)) return prev;
                if (editorsPicksOnly && !checked) {
                    const restored = rows.find((row) => row.storyId === storyId);
                    if (!restored || prev.puzzles.some((row) => row.storyId === storyId)) {
                        return prev;
                    }
                    return {
                        ...prev,
                        puzzles: [...prev.puzzles, { ...restored, editorsPicks: previous }],
                        total: (Number(prev.total) || 0) + 1,
                    };
                }
                return {
                    ...prev,
                    puzzles: prev.puzzles.map((row) =>
                        row.storyId === storyId ? { ...row, editorsPicks: previous } : row
                    ),
                };
            });
        } finally {
            setChoiceBusy((prev) => {
                const next = new Set(prev);
                next.delete(storyId);
                return next;
            });
        }
    }, [rows, editorsPicksOnly]);

    const handleFreeChange = useCallback(async (storyId, checked) => {
        if (!storyId) return;
        setChoiceError(null);
        setChoiceBusy((prev) => {
            const next = new Set(prev);
            next.add(storyId);
            return next;
        });
        const previous = rows.find((row) => row.storyId === storyId)?.free === true;
        setResult((prev) => {
            if (!prev || !Array.isArray(prev.puzzles)) return prev;
            return {
                ...prev,
                puzzles: prev.puzzles.map((row) =>
                    row.storyId === storyId ? { ...row, free: checked } : row
                ),
            };
        });
        try {
            const response = await AdminSlidingService.setStoryTemplatesFree(storyId, checked);
            if (!response?.success) {
                throw new Error(response?.error || "Failed to update free status");
            }
        } catch (err) {
            Logger.error("Failed to update sliding free status:", err);
            setChoiceError("Failed to update free status.");
            setResult((prev) => {
                if (!prev || !Array.isArray(prev.puzzles)) return prev;
                return {
                    ...prev,
                    puzzles: prev.puzzles.map((row) =>
                        row.storyId === storyId ? { ...row, free: previous } : row
                    ),
                };
            });
        } finally {
            setChoiceBusy((prev) => {
                const next = new Set(prev);
                next.delete(storyId);
                return next;
            });
        }
    }, [rows]);

    const openPuzzle = useCallback((storyId) => {
        if (storyId) navigate(`/admin/sliding/${storyId}`);
    }, [navigate]);

    const columns = useMemo(
        () => [
            {
                field: "rank",
                headerName: "Rank",
                flex: 0.4,
                minWidth: 56,
                sortable: false,
            },
            {
                field: "title",
                headerName: "Puzzle",
                flex: 2.2,
                minWidth: 220,
                sortable: false,
                renderCell: (params) => {
                    const imageUrl = params.row?.imageUrl;
                    const thumbSx = {
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                        borderRadius: 1,
                        border: 1,
                        borderColor: "divider",
                        flexShrink: 0,
                    };
                    return imageUrl ? (
                        <Box component="img" src={imageUrl} alt="" sx={{ ...thumbSx, my: 0.75 }} />
                    ) : (
                        <Box sx={{ ...thumbSx, my: 0.75, bgcolor: "action.hover" }} />
                    );
                },
            },
            {
                field: "editorsPicks",
                headerName: "Editor's picks",
                flex: 0.85,
                minWidth: 120,
                sortable: false,
                renderCell: (params) => {
                    const storyId = params.row?.storyId;
                    return (
                        <Box
                            onClick={(event) => event.stopPropagation()}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                minWidth: 44,
                                minHeight: 44,
                            }}
                        >
                            <Checkbox
                                checked={params.row?.editorsPicks === true}
                                disabled={!storyId || choiceBusy.has(storyId)}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => {
                                    event.stopPropagation();
                                    handleEditorsPicks(storyId, event.target.checked);
                                }}
                                slotProps={{ input: { "aria-label": "Editor's picks" } }}
                            />
                        </Box>
                    );
                },
            },
            {
                field: "tags",
                headerName: "Tags",
                flex: 1,
                minWidth: 120,
                sortable: false,
                renderCell: (params) => {
                    const tags = Array.isArray(params.value) ? params.value : [];
                    if (!tags.length) {
                        return (
                            <Typography variant="body2" color="text.secondary">
                                {"\u2014"}
                            </Typography>
                        );
                    }
                    return (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.75 }}>
                            {tags.map((tag) => (
                                <Chip key={tag} size="small" variant="outlined" label={tag} />
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: "completedPlays",
                headerName: "Completed",
                flex: 0.8,
                minWidth: 88,
                type: "number",
                sortable: !editorsPicksOnly,
                sortingOrder: ["desc", "asc"],
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "plays",
                headerName: "Plays",
                flex: 0.7,
                minWidth: 72,
                type: "number",
                sortable: !editorsPicksOnly,
                sortingOrder: ["desc", "asc"],
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "players",
                headerName: "Signed-in",
                flex: 0.75,
                minWidth: 80,
                type: "number",
                sortable: false,
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "guestPlays",
                headerName: "Guest plays",
                flex: 0.85,
                minWidth: 88,
                type: "number",
                sortable: false,
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "state",
                headerName: "State",
                flex: 0.7,
                minWidth: 88,
                sortable: false,
                renderCell: (params) =>
                    params.value ? (
                        <Chip size="small" color={stateChipColor(params.value)} label={params.value} />
                    ) : (
                        "\u2014"
                    ),
            },
            {
                field: "free",
                headerName: "Free",
                flex: 0.55,
                minWidth: 72,
                sortable: false,
                renderCell: (params) => {
                    const storyId = params.row?.storyId;
                    const isFree = params.row?.free === true;
                    return (
                        <Box
                            onClick={(event) => event.stopPropagation()}
                            sx={{ display: "flex", alignItems: "center", minWidth: 44, minHeight: 44 }}
                        >
                            <Checkbox
                                checked={isFree}
                                disabled={!storyId || choiceBusy.has(storyId)}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => {
                                    event.stopPropagation();
                                    handleFreeChange(storyId, event.target.checked);
                                }}
                                slotProps={{ input: { "aria-label": isFree ? "Free, click to set paid" : "Paid, click to set free" } }}
                            />
                        </Box>
                    );
                },
            },
            {
                field: "lastPlayedAt",
                headerName: "Last play",
                flex: 1,
                minWidth: 120,
                sortable: false,
                valueFormatter: (value) => formatDateTime(value),
            },
            {
                field: "publishedAt",
                headerName: "Published",
                flex: 1,
                minWidth: 120,
                sortable: false,
                valueFormatter: (value) => formatDateTime(value),
            },
            {
                field: "createdAt",
                headerName: "Created",
                flex: 1,
                minWidth: 120,
                sortable: false,
                valueFormatter: (value) => formatDateTime(value),
            },
        ],
        [choiceBusy, handleEditorsPicks, handleFreeChange, editorsPicksOnly]
    );

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                height: viewMode === "card" ? "100%" : "auto",
                minHeight: viewMode === "card" ? 0 : "auto",
                width: "100%",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2, pt: 1, flexShrink: 0 }}>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={viewMode}
                    onChange={handleViewModeChange}
                    aria-label="Top puzzles view"
                >
                    <ToggleButton value="list" aria-label="List view" title="List" sx={{ px: 1, minWidth: 44, minHeight: 40 }}>
                        <ViewListIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="card" aria-label="Card view" title="Cards" sx={{ px: 1, minWidth: 44, minHeight: 40 }}>
                        <ViewModuleIcon fontSize="small" />
                    </ToggleButton>
                </ToggleButtonGroup>

                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="sliding-top-puzzles-range-label" shrink>
                        Duration
                    </InputLabel>
                    <Select
                        labelId="sliding-top-puzzles-range-label"
                        label="Duration"
                        notched
                        value={range}
                        onChange={(event) => handleRangeChange(event.target.value)}
                    >
                        {RANGE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="sliding-top-puzzles-order-label" shrink>
                        Order by
                    </InputLabel>
                    <Select
                        labelId="sliding-top-puzzles-order-label"
                        label="Order by"
                        notched
                        value={orderBy}
                        onChange={(event) => handleOrderByChange(event.target.value)}
                    >
                        {ORDER_BY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={editorsPicksOnly}
                            onChange={(event) => resetPage(() => setEditorsPicksOnly(event.target.checked))}
                        />
                    }
                    label="Editor's picks"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeTest}
                            onChange={(event) => resetPage(() => setExcludeTest(event.target.checked))}
                        />
                    }
                    label="Exclude test accounts"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeStaff}
                            onChange={(event) => resetPage(() => setExcludeStaff(event.target.checked))}
                        />
                    }
                    label="Exclude staff"
                />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexShrink: 0 }}>
                Use <strong>Order by</strong> for completed, plays, published, or created (newest/oldest).
                Ordered by <strong>Completed (most)</strong>, ranks <strong>1-5</strong> use an amber
                background (top completed) - except puzzles already marked{" "}
                <strong>Editor&apos;s choice</strong>, which only use teal so they are not counted in both
                sections. <strong>Plays</strong> counts every session; <strong>Completed</strong> is finishes
                only.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                    {error}
                </Alert>
            )}

            {choiceError && (
                <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                    {choiceError}
                </Alert>
            )}

            {!error && result && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                        gap: 1,
                        mb: 2,
                        flexShrink: 0,
                    }}
                >
                    <StatsSummaryTile
                        label="Puzzles"
                        value={formatNumber(result.total)}
                        hint={
                            editorsPicksOnly
                                ? "Editor's picks only"
                                : "Stories with at least one play"
                        }
                    />
                    <StatsSummaryTile label="Plays" value={formatNumber(result.playTotal)} />
                    <StatsSummaryTile label="Completed" value={formatNumber(result.completedTotal)} />
                </Box>
            )}

            <Box
                sx={{
                    flex: viewMode === "card" ? 1 : "0 0 auto",
                    minHeight: viewMode === "card" ? 0 : "auto",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {viewMode === "card" ? (
                    <>
                        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}>
                            {loading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                                    <CircularProgress size={32} />
                                </Box>
                            ) : rows.length === 0 ? (
                                <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                    {editorsPicksOnly ? "No editor's picks yet." : "No plays yet."}
                                </Typography>
                            ) : (
                                <Grid container spacing={1.5}>
                                    {rows.map((row) => (
                                        <Grid key={row.storyId} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                                            <TopPuzzleCard
                                                row={row}
                                                choiceBusy={choiceBusy}
                                                highlightTopFive={highlightTopFive}
                                                onEditorsPicks={handleEditorsPicks}
                                                onFreeChange={handleFreeChange}
                                                onOpen={openPuzzle}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                        <TablePagination
                            component="div"
                            count={rowCount}
                            page={pageModel.page}
                            onPageChange={(_event, page) => setPageModel((prev) => ({ ...prev, page }))}
                            rowsPerPage={pageModel.pageSize}
                            onRowsPerPageChange={(event) => {
                                const pageSize = parseInt(event.target.value, 10);
                                setPageModel({ page: 0, pageSize: Number.isFinite(pageSize) ? pageSize : 25 });
                            }}
                            rowsPerPageOptions={[15, 25, 50]}
                            sx={{ flexShrink: 0, borderTop: 1, borderColor: "divider", mt: 1 }}
                        />
                    </>
                ) : (
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.storyId}
                        rowCount={rowCount}
                        loading={loading}
                        paginationMode="server"
                        sortingMode="server"
                        sortModel={sortModel}
                        onSortModelChange={(model) => {
                            const next = model.length ? model : DEFAULT_SORT_MODEL;
                            const field = next[0]?.field === "plays" ? "plays" : "completedPlays";
                            const dir = next[0]?.sort === "asc" ? "asc" : "desc";
                            handleOrderByChange(orderByValue(field, dir));
                        }}
                        paginationModel={pageModel}
                        onPaginationModelChange={setPageModel}
                        pageSizeOptions={[15, 25, 50]}
                        autoHeight
                        disableRowSelectionOnClick
                        hideFooterSelectedRowCount
                        getRowHeight={() => "auto"}
                        getEstimatedRowHeight={() => 124}
                        getRowClassName={(params) => {
                            const editorsPicks = params.row?.editorsPicks === true;
                            const classes = [];
                            if (highlightTopFive && isTopFiveRank(params.row) && !editorsPicks) {
                                classes.push("sliding-top-five");
                            }
                            if (editorsPicks) {
                                classes.push("sliding-editors-picks");
                            }
                            return classes.join(" ");
                        }}
                        onRowClick={(params, event) => {
                            if (event?.target?.closest?.(".MuiCheckbox-root")) return;
                            if (params?.id) openPuzzle(params.id);
                        }}
                        sx={{
                            "& .MuiDataGrid-virtualScroller": { overflowY: "hidden" },
                            "& .MuiDataGrid-row": { cursor: "pointer" },
                            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                            "& .sliding-top-five": {
                                bgcolor: TOP_FIVE_BG,
                                "&:hover": { bgcolor: TOP_FIVE_BG_HOVER },
                            },
                            "& .sliding-editors-picks": {
                                bgcolor: EDITORS_CHOICE_BG,
                                "&:hover": { bgcolor: EDITORS_CHOICE_BG_HOVER },
                            },
                        }}
                        localeText={{
                            noRowsLabel: loading
                                ? "Loading..."
                                : editorsPicksOnly
                                  ? "No editor's picks yet."
                                  : "No plays yet.",
                        }}
                    />
                )}
            </Box>
        </Box>
    );
}
